import { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import SearchableDropdown from '../../../../components/SearchableDropdown';
import WingFlatPicker from '../components/vms/WingFlatPicker';
import { labelCls, errorCls, hintCls, controlCls } from '../components/form/formStyles';
import { CAB_TYPES, CAB_STATUSES, CAB_VEHICLE_TYPES, toOptions, toLocalInput } from '../vmsConstants';
import { normalizePhone, isValidPhone } from '../visitorConstants';

const TYPE_OPTIONS = toOptions(CAB_TYPES);
const STATUS_OPTIONS = toOptions(CAB_STATUSES);
// Deliberately the cab-only enum: cab logs reject SCOOTER, BICYCLE and TRUCK,
// which the visit and gate-pass forms accept.
const VEHICLE_OPTIONS = toOptions(CAB_VEHICLE_TYPES);

const EMPTY = {
    wing_id: '',
    flat_id: '',
    cab_type: 'PICKUP',
    provider_name: '',
    driver_name: '',
    driver_phone: '',
    vehicle_number: '',
    vehicle_type: '',
    vehicle_model: '',
    booking_id: '',
    status: 'EXPECTED',
    expected_at: '',
    entry_gate: '',
    exit_gate: '',
    notes: '',
};

// Same pattern as deliveries: a gate-desk action, so it lives in a modal.
// On edit, arrived_at / departed_at / exit_gate join the editable set and the
// status field is how a cab reaches WAITING, NO_SHOW or CANCELLED.
const CabFormModal = ({ open, societyId, initial, mode = 'create', submitting, onSubmit, onCancel }) => {
    const [form, setForm] = useState(EMPTY);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (open) {
            setForm({ ...EMPTY, ...(initial || {}) });
            setErrors({});
        }
    }, [open, initial]);

    if (!open) return null;

    const set = (k, v) => {
        setForm(f => ({ ...f, [k]: v }));
        setErrors(e => ({ ...e, [k]: undefined }));
    };

    const validate = () => {
        const e = {};
        if (mode === 'create') {
            if (!form.wing_id) e.wing_id = 'Wing is required';
            if (!form.flat_id) e.flat_id = 'Flat is required';
        }
        if (form.driver_phone.trim() && !isValidPhone(form.driver_phone)) {
            e.driver_phone = 'Enter a 10-digit number, or a full number with country code';
        }
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleSubmit = (ev) => {
        ev.preventDefault();
        if (!validate()) return;

        const payload = { cab_type: form.cab_type, status: form.status };
        [['provider_name', form.provider_name], ['driver_name', form.driver_name],
         ['vehicle_number', form.vehicle_number], ['vehicle_model', form.vehicle_model],
         ['booking_id', form.booking_id], ['entry_gate', form.entry_gate], ['notes', form.notes]]
            .forEach(([k, v]) => { if (v && v.trim()) payload[k] = v.trim(); });
        if (form.driver_phone.trim()) payload.driver_phone = normalizePhone(form.driver_phone);
        if (form.vehicle_type) payload.vehicle_type = form.vehicle_type;
        if (form.expected_at) payload.expected_at = new Date(form.expected_at).toISOString();
        if (mode === 'edit' && form.exit_gate.trim()) payload.exit_gate = form.exit_gate.trim();

        if (mode === 'create') {
            payload.society_id = Number(societyId);
            payload.wing_id = Number(form.wing_id);
            payload.flat_id = Number(form.flat_id);
        }

        onSubmit(payload);
    };

    const unwrap = (v) => (v === 'all' ? '' : v);

    return (
        <div className="tw-fixed tw-inset-0 tw-bg-black/40 tw-flex tw-items-center tw-justify-center tw-z-50 tw-p-4 tw-overflow-y-auto">
            <div className="tw-bg-white tw-rounded-xl tw-shadow-xl tw-w-full tw-max-w-2xl tw-my-8">
                <div className="tw-p-4 tw-border-b tw-border-gray-100 tw-flex tw-items-center tw-justify-between tw-gap-3">
                    <div>
                        <h3 className="tw-mb-0 tw-text-sm tw-font-bold tw-text-gray-800">
                            {mode === 'create' ? 'Log a cab' : 'Edit cab log'}
                        </h3>
                        <p className="tw-mb-0 tw-text-[11px] tw-text-gray-500 tw-mt-0.5">
                            {mode === 'create'
                                ? 'The flat’s residents are notified when it is logged'
                                : initial?.cab_code || ''}
                        </p>
                    </div>
                    <button onClick={onCancel} className="tw-p-1 tw-text-gray-400 hover:tw-text-gray-700">
                        <Icon icon="mdi:close" className="tw-text-lg" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="tw-p-5 tw-space-y-4">
                    <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-4 tw-gap-3">
                        {mode === 'create' ? (
                            <WingFlatPicker
                                societyId={societyId}
                                wingId={form.wing_id}
                                flatId={form.flat_id}
                                onWingChange={(v) => set('wing_id', v)}
                                onFlatChange={(v) => set('flat_id', v)}
                                errors={errors}
                            />
                        ) : (
                            <div className="sm:tw-col-span-2">
                                <label className={labelCls}>Flat</label>
                                <p className="tw-mb-0 tw-px-3 tw-py-2 tw-text-xs tw-text-gray-600 tw-bg-gray-50 tw-border tw-border-gray-200 tw-rounded-md">
                                    {initial?.flatLabel || '—'}
                                </p>
                            </div>
                        )}
                        <div>
                            <label className={labelCls}>Trip type</label>
                            <SearchableDropdown
                                small
                                clearable={false}
                                value={form.cab_type}
                                onChange={(v) => set('cab_type', v)}
                                options={TYPE_OPTIONS}
                                placeholder="Select"
                            />
                        </div>
                        <div>
                            <label className={labelCls}>Status</label>
                            <SearchableDropdown
                                small
                                clearable={false}
                                value={form.status}
                                onChange={(v) => set('status', unwrap(v) || 'EXPECTED')}
                                options={STATUS_OPTIONS}
                                placeholder="Select"
                            />
                            {mode === 'edit' && <p className={hintCls}>Waiting and no-show are set here</p>}
                        </div>
                    </div>

                    <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-3 tw-gap-3">
                        <div>
                            <label className={labelCls}>Provider</label>
                            <input
                                type="text"
                                value={form.provider_name}
                                onChange={e => set('provider_name', e.target.value)}
                                maxLength={100}
                                placeholder="e.g. Uber, Ola"
                                className={controlCls(false)}
                            />
                        </div>
                        <div>
                            <label className={labelCls}>Driver</label>
                            <input
                                type="text"
                                value={form.driver_name}
                                onChange={e => set('driver_name', e.target.value)}
                                maxLength={150}
                                placeholder="optional"
                                className={controlCls(false)}
                            />
                        </div>
                        <div>
                            <label className={labelCls}>Driver phone</label>
                            <input
                                type="tel"
                                inputMode="tel"
                                value={form.driver_phone}
                                onChange={e => set('driver_phone', e.target.value)}
                                maxLength={20}
                                placeholder="optional"
                                className={`${controlCls(!!errors.driver_phone)} tw-font-mono`}
                            />
                            {errors.driver_phone && <p className={errorCls}>{errors.driver_phone}</p>}
                        </div>
                    </div>

                    <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-4 tw-gap-3">
                        <div>
                            <label className={labelCls}>Vehicle number</label>
                            <input
                                type="text"
                                value={form.vehicle_number}
                                onChange={e => set('vehicle_number', e.target.value.toUpperCase())}
                                maxLength={20}
                                placeholder="MH12AB1234"
                                className={`${controlCls(false)} tw-font-mono`}
                            />
                        </div>
                        <div>
                            <label className={labelCls}>Vehicle type</label>
                            <SearchableDropdown
                                small
                                value={form.vehicle_type}
                                onChange={(v) => set('vehicle_type', unwrap(v))}
                                options={VEHICLE_OPTIONS}
                                placeholder="None"
                            />
                        </div>
                        <div>
                            <label className={labelCls}>Model</label>
                            <input
                                type="text"
                                value={form.vehicle_model}
                                onChange={e => set('vehicle_model', e.target.value)}
                                maxLength={100}
                                placeholder="e.g. Swift Dzire"
                                className={controlCls(false)}
                            />
                        </div>
                        <div>
                            <label className={labelCls}>Booking ID</label>
                            <input
                                type="text"
                                value={form.booking_id}
                                onChange={e => set('booking_id', e.target.value)}
                                maxLength={100}
                                placeholder="optional"
                                className={`${controlCls(false)} tw-font-mono`}
                            />
                        </div>
                    </div>

                    <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-3 tw-gap-3">
                        <div>
                            <label className={labelCls}>Expected at</label>
                            <input
                                type="datetime-local"
                                value={toLocalInput(form.expected_at) || form.expected_at}
                                onChange={e => set('expected_at', e.target.value)}
                                className={controlCls(false)}
                            />
                        </div>
                        <div>
                            <label className={labelCls}>Entry gate</label>
                            <input
                                type="text"
                                value={form.entry_gate}
                                onChange={e => set('entry_gate', e.target.value)}
                                maxLength={50}
                                placeholder="e.g. Main Gate"
                                className={controlCls(false)}
                            />
                        </div>
                        {mode === 'edit' && (
                            <div>
                                <label className={labelCls}>Exit gate</label>
                                <input
                                    type="text"
                                    value={form.exit_gate}
                                    onChange={e => set('exit_gate', e.target.value)}
                                    maxLength={50}
                                    placeholder="optional"
                                    className={controlCls(false)}
                                />
                            </div>
                        )}
                    </div>

                    <div>
                        <label className={labelCls}>Notes</label>
                        <textarea
                            rows={2}
                            value={form.notes}
                            onChange={e => set('notes', e.target.value)}
                            maxLength={1000}
                            placeholder="Anything worth recording"
                            className={`${controlCls(false)} tw-resize-none`}
                        />
                    </div>

                    <div className="tw-flex tw-justify-end tw-gap-2 tw-pt-2 tw-border-t tw-border-gray-100">
                        <button
                            type="button"
                            onClick={onCancel}
                            className="tw-px-3 tw-py-2 tw-text-xs tw-font-medium tw-text-gray-600 tw-border tw-border-gray-200 tw-rounded-md hover:tw-bg-gray-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={submitting}
                            className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-2 tw-text-xs tw-font-medium tw-text-white tw-bg-sky-600 hover:tw-bg-sky-700 tw-rounded-md disabled:tw-opacity-50"
                        >
                            <Icon icon="mdi:taxi" className="tw-text-base" />
                            {submitting ? 'Saving...' : (mode === 'create' ? 'Log cab' : 'Save changes')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CabFormModal;
